import React, { useMemo, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import DataTable, { type Column } from '../components/DataTable';
import SlidePanel from '../components/SlidePanel';
import { useAuditLogs } from '../hooks/useAdminApi50';
import type { AuditLog } from '@rule42/billing-types';

export default function AuditLogPage(): React.JSX.Element {
  const [actionFilter, setActionFilter] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState('');
  const [detailLog, setDetailLog] = useState<AuditLog | null>(null);
  const query = useMemo(
    () => ({
      limit: 50,
      ...(actionFilter.trim() ? { action: actionFilter.trim() } : {}),
      ...(resourceTypeFilter.trim() ? { resource_type: resourceTypeFilter.trim() } : {}),
    }),
    [actionFilter, resourceTypeFilter],
  );
  const { data, isLoading, error } = useAuditLogs(query);

  const columns: Column<AuditLog>[] = [
    {
      key: 'created_at',
      header: 'When',
      render: (row) => <span className="text-slate-700">{new Date(row.created_at).toLocaleString()}</span>,
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) => <span className="font-medium text-slate-900">{row.action}</span>,
    },
    {
      key: 'resource_type',
      header: 'Resource',
      render: (row) => <span className="text-slate-700">{row.resource_type}</span>,
    },
    {
      key: 'actor',
      header: 'Actor',
      render: (row) => <span className="text-slate-700">{row.actorId ?? 'system'}</span>,
    },
    {
      key: 'tenant',
      header: 'Tenant',
      render: (row) => <span className="text-slate-700">{row.tenantId}</span>,
    },
  ];

  return (
    <AdminLayout title="Audit Log">
      <div className="space-y-6">
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Compliance and diagnostics</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Audit trail</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Query immutable billing audit events to understand what changed, when it changed, and which actor or system initiated it.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-700">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Matching events</p>
              <p className="mt-1 text-2xl font-semibold text-slate-950">{data?.meta.total ?? 0}</p>
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-2">
            <input
              type="text"
              value={actionFilter}
              onChange={(event) => setActionFilter(event.target.value)}
              placeholder="Filter by action"
              className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700"
            />
            <input
              type="text"
              value={resourceTypeFilter}
              onChange={(event) => setResourceTypeFilter(event.target.value)}
              placeholder="Filter by resource type"
              className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700"
            />
          </div>
        </section>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error.message}
          </div>
        )}

        <DataTable
          columns={columns}
          rows={data?.data ?? []}
          rowKey={(row) => row.id}
          loading={isLoading}
          emptyMessage="No audit events found."
          onRowClick={(row) => setDetailLog(row)}
        />
      </div>

      <SlidePanel
        open={detailLog !== null}
        onClose={() => setDetailLog(null)}
        size="md"
        title={detailLog?.action ?? ''}
        subtitle={detailLog?.resource_type ?? undefined}
        category="Audit Event"
      >
        {detailLog && (
          <div className="px-6 py-6 space-y-4">
            <div className="grid gap-3 grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Action</p>
                <p className="mt-1.5 font-medium text-slate-900">{detailLog.action}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Resource</p>
                <p className="mt-1.5 text-sm text-slate-700">{detailLog.resource_type}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Timestamp</p>
                <p className="mt-1.5 text-sm text-slate-700">{new Date(detailLog.created_at).toLocaleString()}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Resource ID</p>
                <p className="mt-1.5 font-mono text-xs text-slate-600">{detailLog.resourceId}</p>
              </div>
            </div>
            {detailLog.after_state && (
              <div className="rounded-xl border border-slate-100 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">After State</p>
                <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-200">
                  <code>{JSON.stringify(detailLog.after_state, null, 2)}</code>
                </pre>
              </div>
            )}
            <div className="rounded-xl border border-slate-100 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Event ID</p>
              <p className="mt-1 font-mono text-xs text-slate-600">{detailLog.id}</p>
            </div>
          </div>
        )}
      </SlidePanel>
    </AdminLayout>
  );
}
